import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { AppState } from '../../../store';
import { onGetAllProducts } from '../../../store/actions/productActions';
import LinearProgressBar from '../../../common/LinearProgress';
import { SET_ALL_PRODUCTS } from '../../../store/types/actions/ProductAction';
import { dateFormatToLocalString } from '../../../utils';
import { showErrorMessage, showSuccessMessage } from '../../../common/Toaster';
import jwtAxios from '../../../services/axiosInstance/ApiConfig';
import { getHeaders } from '../../../store/actions/Auth';
import { onGetAllCategories, onGetAllSubCategories } from '../../../store/actions/Category';
import FormDialog from '../../../common/FormDialog';

interface Column {
	id: 'id' | 'name' | 'image' | 'brand' | 'category' | 'subcategory' | 'price' | 'colors' | 'size' | 'countInStock' | 'rating' | 'numReviews' | 'created' | 'updated' | 'actions';
	label: string;
	minWidth?: number;
	align?: 'left' | 'right' | 'center';
	format?: (value: number) => string;
}

const columns: readonly Column[] = [
	{ id: 'id', label: 'Product Id', minWidth: 40 },
	{ id: 'name', label: 'Name', minWidth: 170 },
	{ id: 'image', label: 'Product Image', minWidth: 210 },
	{ id: 'brand', label: 'Brand', minWidth: 80 },
	{ id: 'category', label: 'Category', minWidth: 140 },
	{ id: 'subcategory', label: 'Sub Category', minWidth: 140 },
    { id: 'price', label: 'Price', minWidth: 60 },
	{ id: 'colors', label: 'Colors', minWidth: 240 },
	{ id: 'size', label: 'Size', minWidth: 150 },
	{ id: 'countInStock', label: 'Count In Stock', minWidth: 120 },
    { id: 'rating', label: 'Rating', minWidth: 60 },
	{ id: 'numReviews', label: 'Total Reviews', minWidth: 60 },
    { id: 'created', label: 'Created', minWidth: 120 },
    { id: 'updated', label: 'Updated', minWidth: 120 },
	{ id: 'actions', label: 'Actions', minWidth: 90 },
];

interface RowType {
	id: string;
	name: string;
	image: string;
	brand: string;
	category: string;
	subcategory: string;
    price: string;
	colors: string;
	size: string;
	countInStock: number;
    rating: number;
	numReviews: number;
    created: string;
    updated: string;
	actions: string;
}

function createRows(
	id: string,
	name: string,
	image: string,
	brand: string,
	category: string,
	subcategory: string,
    price: string,
	colors: string,
	size: string,
    countInStock: number,
    rating: number,
	numReviews: number,
    created: string,
    updated: string,
	actions: string,
): RowType {
	return { id, name, image, brand, category, subcategory, price, colors, size, countInStock, rating, numReviews, created, updated, actions };
}

interface ProductListsProps {
	handleSelectedProduct: (product: any) => void
}

export default function ProductLists(props: ProductListsProps) {
	const dispatch = useDispatch()
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
	const [deletingRow, setDeletingRow] = useState({ id: '', name: '' })
	const [open, toggleDialog] = useState(false)

	const {products} = useSelector<AppState, AppState['products']>(
        ({products}) => products,
    );

	const {loading} = useSelector<AppState, AppState['common']>(
        ({common}) => common,
    );

	useEffect(() => {
		if(products && products.products.length >= 0) {
			return 
		} else {
			dispatch({ type: SET_ALL_PRODUCTS, payload: null });
			dispatch(onGetAllProducts({}))
			dispatch(onGetAllCategories())
			dispatch(onGetAllSubCategories())
		}
	}, [dispatch, products])

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const handleDeleteProduct = (rowId: string, name: string) => {
		setDeletingRow({ id: rowId, name })
		toggleDialog(true)
	}

	const handleDeleteProceed = () => {
		const { id } = deletingRow
		jwtAxios.delete('/products/deleteProduct/' + id, { headers: getHeaders()})
		.then(response => {
			showSuccessMessage(response.data.message)
			toggleDialog(false)
			dispatch(onGetAllProducts({}))
		}).catch((err: any) => {
			showErrorMessage(err)
		})
	}

	const rows: any =
		products && products.products &&
		products.products.length > 0 
		? products.products.map((product: any) => {
			const comma_sep_colors = product.colors.join(", ")
			const comma_sep_size = product.size.join(", ")
			return createRows(
				product._id,
				product.name,
				product.image,
				product.brand,
				product.category,
				product.subcategory,
				product.price,
				comma_sep_colors,
				comma_sep_size,
				product.countInStock,
				product.rating,
				product.numReviews,
				product.createdAt,
				product.updatedAt,
				product.actions
			)}
		)
		: [];

	return (<>
		<FormDialog
			title={`Do you really want to delete ${deletingRow.name} ?`}
			open={open}
			actions={open}
			toggleDialog={toggleDialog}
			handleProceed={handleDeleteProceed}
		/>
		<Paper sx={{ width: '100%', overflow: 'hidden', padding: 0 }}>
			<TableContainer>
				{loading && <LinearProgressBar />}
				<Table stickyHeader aria-label="sticky table" size='small'>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: column.minWidth, fontWeight: 600, background: 'grey', color: '#fff' }}
								>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows
							.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							.map((row: RowType) => {
								return (
									<TableRow hover role="checkbox" tabIndex={-1} key={row.name}>
										{columns.map((column) => {
											const col_id = column.id
											let value = row[col_id];
											if(col_id === 'created' || col_id === 'updated') {
												value = dateFormatToLocalString(row[col_id])
											}
											return (
												<TableCell key={column.id} align={column.align}>
													{col_id === 'actions' && 
														<Box display="flex" flexDirection="row">
															<EditOutlinedIcon
																color="info"
																sx={{marginRight: '8px'}}
																onClick={()=> props.handleSelectedProduct(row)}
															/>
															<DeleteOutlinedIcon
																color="error"
																onClick={()=> handleDeleteProduct(row.id, row.name)}
															/>
														</Box>
													}
													{col_id !== 'actions' && column.format && typeof value === 'number'
														? column.format(value)
														: value}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[7, 21, 100]}
				component="div"
				count={rows.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</Paper>
		</>
	);
}
